import { Component, Output, OnInit, OnDestroy, EventEmitter } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { AppState } from "@store/app-state";
import { Subscription } from "rxjs/Subscription";
import { Enquiry } from "@models/enquiry";
import { EnquiriesStore } from "@store/enquiries";
import { ConstantsStore } from "@store/constants";
import { Term } from "@models/term";
import { Course } from "@models/course";
import { Campus } from "@models/campus";
import { MatrixClass } from "@models/matrix-class";
import { FETCH_ENQUIRY_CLASSES, ENQUIRY_ACTIVE_DECREMENT_STEP, ENQUIRY_ACTIVE_INCREMENT_STEP } from "@reducers/enquiries";

@Component({
  selector: 'online-enrolment-step-two',
  templateUrl: './online-enrolment-step-two.pug',
})
export class OnlineEnrolmentStepTwoComponent implements OnInit, OnDestroy {

  public enquiry: Enquiry
  public campuses: Campus[]
  public upcomingNormalTerm: Term = null
  public upcomingHolidayTerm: Term = null
  public terms: Term[]

  public classes: MatrixClass[] = []

  public mf: FormGroup
  public hasFormBeenSubmitted: boolean
  
  private enquirySubscription: Subscription
  private constantsSubscription: Subscription
  private mfSubscription: Subscription

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.enquirySubscription =
      this.store.select("enquiriesReducer").subscribe((s: EnquiriesStore) => {
        this.enquiry    = s.activeEnquiry
        this.classes    = s.classes
      })

    this.constantsSubscription =
      this.store.select("constantsReducer").subscribe((s: ConstantsStore) => {
        this.campuses     = s.constants.campuses
        this.terms        = s.constants.terms
        this.setUpcomingTerm()
      })

    this.setupMainForm()
    this.refreshPotentialClasses()
  }

  setupMainForm() {
    this.mf = this.fb.group({
      campusIds:          this.fb.array(this.enquiry.campusIds),
      matrixClassIds:     this.fb.array(this.enquiry.matrixClassIds),
    })

    this.mfSubscription =
      this.mf.valueChanges.subscribe((data) => {
        this.updateModel(data)
      })
  }

  setUpcomingTerm() {
    this.upcomingNormalTerm = this.terms.find((t: Term) => {
      return t.isOnlineEnrolmentEnabled && !t.isHoliday })

    this.upcomingHolidayTerm = this.terms.find((t: Term) => {
      return t.isOnlineEnrolmentEnabled && t.isHoliday })
  }

  isCampusSelected(campus: Campus): boolean {
    return (this.mf.get('campusIds').value as number[]).includes(campus.id)
  }

  clickedCampusOption(targetCampus: Campus) {
    const selectedCampusIds = this.mf.get("campusIds").value as number[]

    let targetCampusIds: number[]
    if (selectedCampusIds.includes(targetCampus.id)) {
      targetCampusIds = selectedCampusIds.filter((cId) => cId != targetCampus.id)
    }
    else {
      targetCampusIds = selectedCampusIds.slice(0)
      targetCampusIds.push(targetCampus.id)
    }

    this.mf.setControl("campusIds", this.fb.array(targetCampusIds))

    this.refreshPotentialClasses()
  }

  clickedClassOption(mc: MatrixClass) {
    const selectedMatrixClassIds = this.mf.get("matrixClassIds").value as number[]
    let targetMatrixClassIds: number[]

    if (selectedMatrixClassIds.includes(mc.id)) {
      targetMatrixClassIds = selectedMatrixClassIds.filter((mcId) => mcId != mc.id)
    }
    else {
      targetMatrixClassIds = selectedMatrixClassIds.slice(0)
      targetMatrixClassIds.push(mc.id)
    }

    this.mf.setControl("matrixClassIds", this.fb.array(targetMatrixClassIds))
  }

  clickedPrevious() {
    this.store.dispatch({ type: ENQUIRY_ACTIVE_DECREMENT_STEP })
  }

  getUniqueCampusIdsFromClasses(): number[] {
    const campusIds: number[] = []
    this.classes.forEach((mc: MatrixClass) => {
      if (!campusIds.includes(mc.campus.id)) { campusIds.push(mc.campus.id) }
    })
    return campusIds
  }

  getCampus(id: number): Campus {
    return this.campuses.find((c: Campus) => c.id == id)
  }

  getNormalClassesByCampus(campusId: number): MatrixClass[] {
    return this.classes.filter((mc: MatrixClass) =>
      { return mc.campus.id == campusId && !mc.term.isHoliday })
  }

  getHolidayClassesByCampus(campusId: number): MatrixClass[] {
    return this.classes.filter((mc: MatrixClass) =>
      { return mc.campus.id == campusId && mc.term.isHoliday })
  }

  areNormalTermClassesAvailable(): boolean {
    return this.classes.filter((mc) => !mc.term.isHoliday).length > 0
  }

  areHolidayTermClassesAvailable(): boolean {
    return this.classes.filter((mc) => mc.term.isHoliday).length > 0
  }

  updateModel(data: any) {
    this.enquiry.campusIds        = data.campusIds
    this.enquiry.matrixClassIds   = data.matrixClassIds
  }

  isClassSelected(mc: MatrixClass): boolean {
    const selectedIds = this.mf.get("matrixClassIds").value as number[]
    return selectedIds.includes(mc.id)
  }

  refreshPotentialClasses() {
    if (this.enquiry.campusIds.length > 0 && this.enquiry.courseIds.length > 0) {

      const termIds = []
      if (this.upcomingHolidayTerm) { termIds.push(this.upcomingHolidayTerm.id) }
      if (this.upcomingNormalTerm)  { termIds.push(this.upcomingNormalTerm.id) }

      const params = {
        termIds: termIds,
        campusIds: this.enquiry.campusIds,
        courseIds: this.enquiry.courseIds,
      }

      this.store.dispatch({ type: FETCH_ENQUIRY_CLASSES, payload: params })
    }
  }

  submittedForm({ value, valid }: { value: any, valid: boolean }) {
    this.hasFormBeenSubmitted = true

    if (this.enquiry.campusIds.length == 0 || 
      this.enquiry.matrixClassIds.length == 0) { return }

    if (valid) {
      this.store.dispatch({ type: ENQUIRY_ACTIVE_INCREMENT_STEP })
    }
  }

  ngOnDestroy() {
    this.enquirySubscription.unsubscribe()
    this.constantsSubscription.unsubscribe()
    this.mfSubscription.unsubscribe()
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/online-enrolment-step-two.ts